<template>
  <dialog :open="isOpen" class="default-dialog">
    <div class="dialog-content">
      <header class="dialog-header">
        <div class="dialog-header-text">
          <h2 class="dialog-title">{{ dialogTitle }}</h2>
          <span v-if="isEditForm" class="dialog-note">
            <strong>Atenção:</strong>
            nos formulários de edição, as alterações são aplicadas
            automaticamente, sem obrigatoriedade de clicar no botão
            'editar campanha' para finalizar suas alterações.
          </span>
          <span v-else class="dialog-note">{{ dialogHint }}</span>
        </div>
        <button
          type="button"
          @click="closeModal"
          class="close-icon-button"
          aria-label="Fechar"
        >
          ✖
        </button>
      </header>

      <ul
        class="awarded-quotes-list"
        v-if="awardedQuotes.length > 0 && isAddingQuote == false"
      >
        <li v-for="(quote, index) in awardedQuotes" :key="index">
          <div class="awarded-quotes-item">
            <div>
              <span class="quote-number">
                <img :src="variantIcon" alt="" class="quote-variant-icon" />
                <strong>Cota Nº:</strong>
                {{ quote.quota }}
              </span>

              <span> <strong>Prêmio:</strong> {{ quote.award }} </span>
            </div>

            <button
              type="button"
              class="remove-quote-btn"
              @click="removeQuote(quote, index)"
            >
              <i class="material-icons">delete</i>
            </button>
          </div>
        </li>
      </ul>

      <div
        v-else-if="awardedQuotes.length == 0 && isAddingQuote == false"
        class="no-quotes"
      >
        <i class="material-icons">sentiment_dissatisfied</i>
        <span>{{ emptyLabel }}</span>
      </div>

      <main class="dialog-body">
        <div v-if="!isAddingQuote">
          <button class="btn mb-0 submit-button" @click.prevent="showQuoteForm">
            <span>{{ addButtonLabel }}</span>
            <i class="material-icons ticket-icon">add</i>
          </button>

          <div v-if="showPrizeRelease" class="prize-release-panel">
            <div class="prize-release-head">
              <div>
                <strong>Liberação automática por % vendido</strong>
                <p class="prize-release-hint">
                  Bloqueia as cotas premiadas e libera aos poucos conforme as
                  vendas.
                </p>
              </div>
              <label class="prize-release-switch">
                <input
                  type="checkbox"
                  :checked="draftEnabled"
                  @change="onToggleRelease($event.target.checked)"
                />
                <span>{{ draftEnabled ? "SIM" : "NÃO" }}</span>
              </label>
            </div>

            <template v-if="draftEnabled">
              <div class="prize-release-stats">
                <span
                  >Vendido:
                  {{ Number(prizeReleaseSoldPct || 0).toFixed(1) }}%</span
                >
                <span
                  >Bloqueadas:
                  {{ prizeReleaseBlocked || awardedQuotes.length }}</span
                >
                <span>Liberadas: {{ prizeReleaseReleased || 0 }}</span>
              </div>

              <div class="prize-release-table-head">
                <span>% vendido</span>
                <span>Qtd a liberar</span>
                <span></span>
              </div>

              <div
                v-if="!localRules.length"
                class="prize-release-empty"
              >
                Nenhuma faixa cadastrada. Clique em
                <strong>+ Adicionar faixa</strong> para começar.
              </div>

              <div
                v-for="(rule, idx) in localRules"
                :key="'pr-modal-' + idx"
                class="prize-release-row"
              >
                <label class="prize-release-field">
                  <span class="prize-release-field-label">Quando atingir</span>
                  <div class="prize-release-input-wrap">
                    <input
                      type="number"
                      min="1"
                      max="100"
                      v-model.number="rule.pct"
                      placeholder="10"
                    />
                    <small>%</small>
                  </div>
                </label>
                <label class="prize-release-field">
                  <span class="prize-release-field-label">Liberar</span>
                  <div class="prize-release-input-wrap">
                    <input
                      type="number"
                      min="0"
                      v-model.number="rule.qty"
                      placeholder="3"
                    />
                    <small>cotas</small>
                  </div>
                </label>
                <button
                  type="button"
                  class="prize-release-remove"
                  @click="removeRule(idx)"
                >
                  Remover
                </button>
              </div>

              <button type="button" class="prize-release-add" @click="addRule">
                + Adicionar faixa
              </button>
              <p class="prize-release-hint mt-2 mb-0">
                Em <strong>% vendido</strong> informe quando liberar. Em
                <strong>Qtd a liberar</strong> informe quantas cotas voltam ao
                pool <em>(0 = libera todas as restantes)</em>.
              </p>
            </template>

            <button
              type="button"
              class="prize-release-save"
              :disabled="isSavingRelease"
              @click="savePrizeRelease"
            >
              {{ isSavingRelease ? "Salvando..." : "Salvar liberação" }}
            </button>
          </div>
        </div>

        <div v-else class="quote-form">
          <div class="input-group input-group-outline m-0 d-flex flex-column">
            <label class="input-label">
              <span
                class="text-xs text-uppercase text-body font-weight-bolder"
                >Número da cota</span
              >
              <input
                v-model.number="newQuote.quota"
                type="text"
                class="form-control default-bordered-input"
                placeholder="Ex: 123"
              />
            </label>
          </div>
          <div class="input-group input-group-outline m-0 d-flex flex-column">
            <label class="input-label">
              <span
                class="text-xs text-uppercase text-body font-weight-bolder"
                >Prêmio</span
              >
              <input
                v-model="newQuote.award"
                type="text"
                class="form-control default-bordered-input"
                placeholder="Ex: R$ 50 no PIX"
              />
            </label>
          </div>

          <div class="button-field">
            <button
              @click="saveQuote"
              class="btn submit-button"
              :disabled="!isFieldsFilled || isSubmitting"
            >
              Salvar
            </button>
            <button @click="cancelQuoteCreation" class="btn cancel-button">
              Cancelar
            </button>
          </div>
        </div>
      </main>
    </div>

    <RemoveDialog
      :is-open="isDeleteDialogOpen"
      @close="isDeleteDialogOpen = false"
      :title="'Remover cota premiada'"
      :removeText="'Esta ação é irreversível e ocorrerá no momento em que confirmar a remoção. Tem certeza que deseja continuar com a deleção desta cota premiada da campanha?'"
      @confirm="handleConfirmDelete"
    />
  </dialog>
</template>

<script>
import { vMaska } from "maska/vue";
import { RouteName } from "/src/store/routeName";
import AwardedQuotesHttp from "/src/http/AwardedQuotesHttp.js";
import RafflesHttp from "/src/http/RafflesHttp.js";
import RemoveDialog from "/src/views/components/RemoveDialog.vue";
import {
  prizeGameIcon,
  prizeGameDialogHint,
  prizeGameEmptyLabel,
  prizeGameAddButtonLabel,
  prizeGameNumbersButtonLabel,
} from "@/constants/prizeGames";

export default {
  directives: {
    maska: vMaska,
  },
  props: {
    isOpen: Boolean,
    receivedData: Array,
    campaignId: Number || null,
    variant: {
      type: String,
      default: "cota",
    },
    quotaKind: {
      type: String,
      default: "cota",
    },
    prizeReleaseEnabled: {
      type: Boolean,
      default: false,
    },
    prizeReleaseRules: {
      type: Array,
      default: () => [],
    },
    prizeReleaseSoldPct: {
      type: Number,
      default: 0,
    },
    prizeReleaseBlocked: {
      type: Number,
      default: 0,
    },
    prizeReleaseReleased: {
      type: Number,
      default: 0,
    },
  },
  emits: [
    "close",
    "update:awardedQuotes",
    "update:prizeReleaseEnabled",
    "update:prizeReleaseRules",
    "prize-release-saved",
  ],
  components: {
    RemoveDialog,
  },
  data() {
    return {
      awardedQuotes: [],
      isAddingQuote: false,
      newQuote: {
        quota: "",
        award: "",
      },
      itemToRemove: null,
      indexToRemove: null,
      isDeleteDialogOpen: false,
      isSubmitting: false,
      draftEnabled: false,
      localRules: [],
      isSavingRelease: false,
    };
  },
  watch: {
    receivedData: {
      handler(data) {
        this.awardedQuotes = Array.isArray(data) ? [...data] : [];
      },
      immediate: true,
      deep: true,
    },
    isOpen(open) {
      if (open) {
        this.awardedQuotes = Array.isArray(this.receivedData)
          ? [...this.receivedData]
          : [];
        this.loadPrizeReleaseDraft();
        this.isAddingQuote = false;
      }
    },
  },
  methods: {
    loadPrizeReleaseDraft() {
      this.draftEnabled = !!this.prizeReleaseEnabled;
      this.localRules = (Array.isArray(this.prizeReleaseRules)
        ? this.prizeReleaseRules
        : []
      ).map((r) => ({
        pct: Number(r?.pct) || 0,
        qty: Number(r?.qty) || 0,
      }));
    },
    onToggleRelease(enabled) {
      this.draftEnabled = !!enabled;
    },
    buildRulesPayload() {
      return (this.localRules || [])
        .map((r) => ({
          pct: Number(r.pct) || 0,
          qty: Number(r.qty) || 0,
        }))
        .filter((r) => r.pct >= 1 && r.pct <= 100);
    },
    addRule() {
      this.localRules.push({ pct: null, qty: null });
    },
    removeRule(idx) {
      this.localRules.splice(idx, 1);
    },
    async savePrizeRelease() {
      const enabled = !!this.draftEnabled;
      const rules = enabled ? this.buildRulesPayload() : [];

      if (enabled && !rules.length) {
        this.$toast.add({
          severity: "warn",
          summary: "Faixa obrigatória",
          detail:
            "Ative a liberação e adicione ao menos uma faixa (% vendido) antes de salvar.",
          life: 3500,
        });
        return;
      }

      this.$emit("update:prizeReleaseEnabled", enabled);
      this.$emit("update:prizeReleaseRules", rules);

      if (this.isEditForm && this.campaignId) {
        try {
          this.isSavingRelease = true;
          const { data } = await RafflesHttp.updatePrizeRelease(this.campaignId, {
            prize_release_enabled: enabled,
            prize_release_rules: rules,
          });
          this.$emit("prize-release-saved", data || {});
          this.$toast.add({
            severity: "success",
            summary: "Liberação salva",
            detail: "A liberação automática foi aplicada nesta campanha.",
            life: 3000,
          });
        } catch (error) {
          this.$toast.add({
            severity: "error",
            summary: "Erro ao salvar",
            detail:
              "Não foi possível salvar a liberação. Tente novamente.",
            life: 3500,
          });
          return;
        } finally {
          this.isSavingRelease = false;
        }
      } else {
        this.$toast.add({
          severity: "success",
          summary: "Liberação configurada",
          detail:
            "Configuração aplicada. Finalize clicando em criar/editar campanha.",
          life: 3500,
        });
      }
    },
    closeModal() {
      this.$emit("close");
      this.resetForm();
    },
    showQuoteForm() {
      this.isAddingQuote = true;
    },
    async saveQuote() {
      if (typeof this.newQuote.quota === "number" && this.newQuote.award) {
        const payloadQuote = {
          ...this.newQuote,
          kind: this.quotaKind || "cota",
        };
        if (this.isEditForm) {
          try {
            this.isSubmitting = true;
            await AwardedQuotesHttp.createAwardedQuote(
              [payloadQuote],
              this.campaignId
            );
          } catch (error) {
            this.$toast.add({
              severity: "error",
              summary: "Ocorreu um erro.",
              detail:
                "Não foi possível criar a cota premiada. Tente novamente.",
              life: 3000,
            });
            this.closeModal();
            return;
          } finally {
            this.isSubmitting = false;
          }
        }

        this.awardedQuotes.push({ ...payloadQuote });
        this.$emit("update:awardedQuotes", this.awardedQuotes);
        this.resetForm();
      } else {
        alert("Por favor, preencha todos os campos.");
      }
    },
    async removeQuote(item, index) {
      if (this.isEditForm) {
        this.itemToRemove = item;
        this.indexToRemove = index;
        this.isDeleteDialogOpen = true;
      } else {
        this.awardedQuotes.splice(index, 1);
        this.$emit("update:awardedQuotes", this.awardedQuotes);
      }
    },
    async handleConfirmDelete() {
      try {
        await AwardedQuotesHttp.deleteAwardedQuote(this.itemToRemove.id);
        this.awardedQuotes.splice(this.indexToRemove, 1);
        this.$emit("update:awardedQuotes", this.awardedQuotes);
      } catch (error) {
        this.$toast.add({
          severity: "error",
          summary: "Ocorreu um erro.",
          detail: "Não foi possível remover a cota premiada.",
          life: 3000,
        });
      }
    },
    cancelQuoteCreation() {
      this.resetForm();
    },
    resetForm() {
      this.newQuote.quota = "";
      this.newQuote.award = "";
      this.isAddingQuote = false;
    },
  },
  computed: {
    showPrizeRelease() {
      return this.variant === "cota";
    },
    dialogTitle() {
      return prizeGameNumbersButtonLabel(this.variant);
    },
    dialogHint() {
      return prizeGameDialogHint(this.variant);
    },
    emptyLabel() {
      return prizeGameEmptyLabel(this.variant);
    },
    addButtonLabel() {
      return prizeGameAddButtonLabel(this.variant);
    },
    variantIcon() {
      return prizeGameIcon(this.variant);
    },
    isFieldsFilled() {
      return (
        typeof this.newQuote.quota == "number" &&
        this.newQuote.award.trim() !== ""
      );
    },
    isEditForm() {
      return this.$route.name == RouteName.EditRaffle;
    },
  },
};
</script>

<style scoped lang="scss">
.default-dialog {
  border: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999999;
  background: none;
  backdrop-filter: blur(4px);
  background-color: rgba(0, 0, 0, 0.2);
  width: 100vw;
  height: 100vh;
}

dialog {
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s, transform 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
}

dialog[open] {
  opacity: 1;
  pointer-events: inherit;
}

dialog::backdrop {
  backdrop-filter: blur(2px);
  animation: fadein 0.2s linear;
}

.dialog-content {
  width: 600px;
  max-width: 100%;
  max-height: 90%;
  overflow: auto;
  background: white;
  border-radius: 8px;
  padding: 20px;
  z-index: 2;
  transform: scale(0.96);
  transition: transform 0.2s;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

dialog[open] .dialog-content {
  transform: scale(1);
}

.dialog-title {
  font-family: sans-serif;
  font-size: 26px;
  color: #111111;
  margin: 0;
}

.dialog-header-text {
  flex: 1;
  min-width: 0;
  padding-right: 12px;
}

.dialog-note {
  display: block;
  font-size: 14px;
  color: #111111;
  margin-top: 6px;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.dialog-body {
  margin: 20px 0 0 0;
}

.close-icon-button {
  flex-shrink: 0;
  background: none;
  border: none;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 4px 6px;
  color: #111111;
  border-radius: 4px;

  &:hover {
    background: #f1f5f9;
  }
}

.button-field {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
}

.cancel-button {
  background-color: #e0e0e0;
  color: #0f172a;
  display: flex;
  height: 40px;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 6px;
  line-height: 0;
  margin-bottom: 0;

  &:hover {
    background-color: #d6d6d6;
    color: #0f172a;
  }
}

.submit-button {
  background-color: #661cb0;
  color: white;
  display: flex;
  height: 40px;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 6px;
  line-height: 0;
  position: relative;
  transition: background-color 0.2s ease;
  cursor: pointer;
  margin-bottom: 0;

  &:hover {
    background-color: #7b23d3;
    color: white;
  }
}

.quote-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.quote-form input {
  padding: 10px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
}

.awarded-quotes-list {
  list-style: none;
  padding: 0;
  margin: 20px 0 0;
}

.awarded-quotes-item {
  border: 1px solid #d2d6da !important;
  padding: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  border-radius: 4px;
  color: #36454f;
}

.quote-number {
  display: flex;
  gap: 5px;
  align-items: center;
}

.quote-variant-icon {
  width: 18px;
  height: 18px;
  object-fit: contain;
  flex-shrink: 0;
}

.no-quotes {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  margin: 40px 0 40px 0;
  color: #3c3d42;
}

.remove-quote-btn {
  border: none;
  background-color: transparent;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.prize-release-panel {
  margin-top: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px;
}

.prize-release-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.prize-release-hint {
  margin: 4px 0 0;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

.prize-release-switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;

  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
}

.prize-release-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 12px 0 10px;

  span {
    font-size: 11px;
    font-weight: 600;
    background: #e2e8f0;
    color: #334155;
    border-radius: 999px;
    padding: 4px 10px;
  }
}

.prize-release-table-head {
  display: grid;
  grid-template-columns: 1fr 1fr 72px;
  gap: 8px;
  margin-bottom: 6px;
  padding: 0 2px;

  span {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: #475569;
  }
}

.prize-release-row {
  display: grid;
  grid-template-columns: 1fr 1fr 72px;
  gap: 8px;
  align-items: end;
  margin-bottom: 8px;
}

.prize-release-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
}

.prize-release-field-label {
  font-size: 11px;
  color: #64748b;
}

.prize-release-input-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 0 10px;
  height: 40px;

  input {
    border: none;
    outline: none;
    width: 100%;
    min-width: 0;
    font-size: 14px;
    background: transparent;
    height: 100%;
  }

  small {
    font-size: 12px;
    font-weight: 700;
    color: #661cb0;
    flex-shrink: 0;
  }
}

.prize-release-remove {
  height: 40px;
  border: 1px solid #fecaca;
  background: #fff;
  color: #b91c1c;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 6px;

  &:hover {
    background: #fef2f2;
  }
}

.prize-release-add {
  width: 100%;
  margin-top: 4px;
  height: 36px;
  border: 1px dashed #c4b5fd;
  background: #f5f3ff;
  color: #5b21b6;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #ede9fe;
  }
}

.prize-release-empty {
  font-size: 12px;
  color: #64748b;
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 8px;
  text-align: center;
}

.prize-release-save {
  width: 100%;
  margin-top: 14px;
  height: 42px;
  border: none;
  background: #661cb0;
  color: #fff;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #7b23d3;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
}

@media (max-width: 520px) {
  .prize-release-table-head,
  .prize-release-row {
    grid-template-columns: 1fr 1fr;
  }

  .prize-release-table-head span:last-child {
    display: none;
  }

  .prize-release-remove {
    grid-column: 1 / -1;
  }
}
</style>
